/**
 * Carte des vins › Composer (/carte) — panneau d'options à gauche (400 px), aperçu A4 à droite.
 * Toute modification enregistre le modèle et rafraîchit l'aperçu (moteur unique CardPreview).
 */
import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Copy, Trash2, Pencil, FileDown, Upload, GripVertical, Wine, Martini, GlassWater } from 'lucide-react';
import { carteApi, fileUrl } from '../../services/api';
import { Button, Spinner } from '../../components/ui/kit';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import CardPreview from './CardPreview';

const LANGS = { fr: 'Français', en: 'English', nl: 'Nederlands', de: 'Deutsch', es: 'Español' };

function Opt({ label, children }) {
  return <label className="block mb-4 pb-4 border-b border-hair last:border-b-0"><span className="kicker block mb-1">{label}</span>{children}</label>;
}
function Seg({ value, onChange, options, testid }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {options.map((o) => (
        <button key={o.value} onClick={() => onChange(o.value)}
          className={`px-2.5 h-8 rounded border text-[12.5px] transition-colors ${value === o.value ? 'bg-gold100 border-gold' : 'border-hair hover:bg-surface'}`}
          data-testid={`${testid}-${o.value}`}>{o.label}</button>
      ))}
    </div>
  );
}
function Check({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-2 text-[13px] cursor-pointer py-0.5" data-testid={testid}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="accent-gold" /> {label}
    </label>
  );
}

const FONTS = ['Georgia', 'Verdana', 'Cormorant Garamond', 'IBM Plex Sans', 'Times', 'Helvetica'];
const SWATCHES = ['#1f3f7a', '#201f1d', '#7a1f2b', '#2e5d3b'];
const TYPO_ROLES = [['region', 'Région'], ['cuvee', 'Ligne cuvée'], ['prix', 'Prix'], ['texte', 'Texte'], ['slogan', 'Slogan']];

/** Onglets de typographie : police, taille (± 0,5 pt, 7–30), couleur (+ hex), gras/italique (slogan). */
function TypoTabs({ typo, onChange }) {
  const [role, setRole] = useState('region');
  const t = typo[role] || {};
  return (
    <div className="mt-3" data-testid="typo-tabs">
      <div className="flex gap-4 border-b border-hair mb-3">
        {TYPO_ROLES.map(([k, label]) => (
          <button key={k} onClick={() => setRole(k)} data-testid={`typo-tab-${k}`}
            className={`pb-1.5 -mb-px text-[12.5px] border-b-2 transition-colors ${role === k ? 'border-gold text-ink font-medium' : 'border-transparent text-n500 hover:text-ink'}`}>{label}</button>
        ))}
      </div>
      <label className="block mb-2"><span className="text-[11px] text-n500">Police</span>
        <select className="vd-input h-8 w-full text-[13px]" value={t.font || 'Georgia'} onChange={(e) => onChange(role, { font: e.target.value })} data-testid="typo-font">
          {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select></label>
      <div className="flex items-center gap-3 mb-2">
        <span className="text-[11px] text-n500">Taille</span>
        <div className="flex items-center gap-1" data-testid="typo-size">
          <button className="w-7 h-7 rounded border border-hair hover:bg-surface" onClick={() => onChange(role, { size: Math.max(7, (t.size || 11) - 0.5) })}>−</button>
          <span className="w-12 text-center nums text-[13px]">{t.size || 11} pt</span>
          <button className="w-7 h-7 rounded border border-hair hover:bg-surface" onClick={() => onChange(role, { size: Math.min(30, (t.size || 11) + 0.5) })}>+</button>
        </div>
      </div>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[11px] text-n500">Couleur</span>
        {SWATCHES.map((c) => (
          <button key={c} onClick={() => onChange(role, { color: c })} data-testid={`typo-color-${c}`}
            className={`w-6 h-6 rounded-full border ${t.color === c ? 'ring-2 ring-gold' : 'border-hair'}`} style={{ backgroundColor: c }} />
        ))}
        <input type="color" value={t.color || '#1f3f7a'} onChange={(e) => onChange(role, { color: e.target.value })} className="w-6 h-6 rounded-full border border-hair cursor-pointer" data-testid="typo-color-custom" />
        <span className="text-[11px] text-n500 nums">{t.color}</span>
      </div>
      {role === 'slogan' && (
        <div className="flex gap-2 mt-1">
          <button onClick={() => onChange(role, { bold: !t.bold })} className={`px-2 h-7 rounded border text-[12px] font-bold ${t.bold ? 'bg-gold100 border-gold' : 'border-hair'}`} data-testid="typo-bold">G</button>
          <button onClick={() => onChange(role, { italic: !t.italic })} className={`px-2 h-7 rounded border text-[12px] italic ${t.italic ? 'bg-gold100 border-gold' : 'border-hair'}`} data-testid="typo-italic">I</button>
        </div>
      )}
    </div>
  );
}

const FluteIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M9 3h6l-.8 8.2a2.2 2.2 0 0 1-4.4 0L9 3Z" /><path d="M12 15v6" /><path d="M9 21h6" />
  </svg>
);
const SYMBOL_OPTIONS = [
  ['verre', 'Verre à vin', Wine], ['ballon', 'Verre ballon', GlassWater],
  ['flute', 'Flûte', FluteIcon], ['coupe', 'Coupe', Martini],
];

/** Sélecteur de pictogramme « verre » (popover), appliqué à l'aperçu et au PDF. */
function SymbolPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const Cur = (SYMBOL_OPTIONS.find(([k]) => k === value) || SYMBOL_OPTIONS[0])[2];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="w-9 h-8 rounded border border-hair hover:bg-surface inline-flex items-center justify-center text-gold800" data-testid="symbol-picker">
          <Cur style={{ width: 18, height: 18 }} strokeWidth={1.75} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-1.5 flex gap-1" data-testid="symbol-picker-menu">
        {SYMBOL_OPTIONS.map(([k, label, Ic]) => (
          <button key={k} title={label} onClick={() => { onChange(k); setOpen(false); }} data-testid={`symbol-opt-${k}`}
            className={`w-9 h-9 rounded border inline-flex items-center justify-center ${value === k ? 'bg-gold100 border-gold text-gold800' : 'border-hair hover:bg-surface'}`}>
            <Ic style={{ width: 18, height: 18 }} strokeWidth={1.75} />
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/** Ordre personnalisé des régions : réordonnable par glissé-déposé (poignée) ou flèches. */
function CustomOrder({ regions, order, onChange }) {
  const list = [...new Set([...(order && order.length ? order.filter((r) => regions.includes(r)) : regions), ...regions.filter((r) => !(order || []).includes(r))])];
  const [drag, setDrag] = useState(null);
  const reorder = (from, to) => {
    if (from === to || from == null || to == null) return;
    const n = [...list];
    const [m] = n.splice(from, 1);
    n.splice(to, 0, m);
    onChange(n);
  };
  return (
    <div className="mt-2 space-y-1" data-testid="custom-order">
      {list.map((r, i) => (
        <div key={r} draggable onDragStart={() => setDrag(i)} onDragEnd={() => setDrag(null)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); reorder(drag, i); setDrag(null); }}
          className={`flex items-center gap-2 px-2 h-8 rounded border text-[13px] bg-white cursor-grab active:cursor-grabbing ${drag === i ? 'border-gold opacity-60' : 'border-hair'}`}
          data-testid={`order-row-${i}`}>
          <GripVertical className="w-3.5 h-3.5 text-n500 shrink-0" />
          <span className="flex-1 truncate">{r}</span>
          <span className="flex gap-1">
            <button onClick={() => reorder(i, i - 1)} className="w-6 h-6 rounded hover:bg-surface" data-testid={`order-up-${i}`}>↑</button>
            <button onClick={() => reorder(i, i + 1)} className="w-6 h-6 rounded hover:bg-surface" data-testid={`order-down-${i}`}>↓</button>
          </span>
        </div>
      ))}
    </div>
  );
}

/** Rangée de pastilles toggle pour les prix (Aucun · Bouteille · Verre · À emporter). */
function PricePill({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 h-8 rounded-full border text-[12.5px] transition-colors ${active ? 'bg-gold100 border-gold text-gold800' : 'border-hair hover:bg-surface'}`}>{children}</button>
  );
}
function PriceRow({ label, children }) {
  return (
    <div className="flex items-center gap-3 mt-2.5">
      <span className="w-16 shrink-0 text-[12px] text-n500">{label}</span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

/** État « aucun modèle » : panneau grisé + bouton « Nouveau modèle » bien visible. */
function EmptyComposer({ onNew }) {
  const SECTIONS = ['Format de page', 'Classement', 'Ordre des régions', 'Ligne cuvée', 'Prix', 'Typographie', 'En-tête et pied de page'];
  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-110px)]">
      <div className="lg:w-[400px] shrink-0 overflow-y-auto p-4 hair-r" data-testid="composer-empty">
        <div className="mb-5 p-5 rounded-lg border border-hair bg-surface text-center">
          <p className="text-[13px] text-n600 mb-3">Aucun modèle de carte.</p>
          <Button variant="primary" onClick={onNew} data-testid="empty-new-model"><Plus className="w-4 h-4" /> Nouveau modèle</Button>
        </div>
        <div className="opacity-40 pointer-events-none select-none space-y-4">
          {SECTIONS.map((l) => (
            <div key={l}>
              <span className="kicker block mb-1.5">{l}</span>
              <div className="h-8 rounded border border-hair bg-surface" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto bg-surface p-4 md:p-8 flex items-center justify-center">
        <p className="text-[13px] text-n500" data-testid="empty-preview">Créez un modèle pour composer votre carte.</p>
      </div>
    </div>
  );
}

export default function Composer() {
  const qc = useQueryClient();
  const { data: templates = [], refetch: refetchT, isLoading: tLoading } = useQuery({ queryKey: ['carte-templates'], queryFn: carteApi.settings });
  const [confirmDel, setConfirmDel] = useState(false);
  const { data: consts } = useQuery({ queryKey: ['carte-consts'], queryFn: carteApi.constants });
  const [id, setId] = useState(null);
  const [s, setS] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (templates.length && !id) { setId(templates[0].id); setS(templates[0]); } }, [templates, id]);
  useEffect(() => { const t = templates.find((x) => x.id === id); if (t && !dirty) setS(t); }, [id]); // eslint-disable-line

  const { data: preview } = useQuery({
    queryKey: ['carte-preview', id, s && JSON.stringify(s)],
    queryFn: () => carteApi.preview(s, 'fr'), enabled: !!s,
  });

  // Enregistrement automatique (anti-rebond ~1 s) : persiste les réglages en cours.
  useEffect(() => {
    if (!dirty || !s) return;
    const t = setTimeout(async () => {
      await carteApi.saveSettings(s);
      setDirty(false);
      refetchT();
      qc.invalidateQueries({ queryKey: ['carte-editions'] });
    }, 1000);
    return () => clearTimeout(t);
  }, [s, dirty]); // eslint-disable-line

  const patch = (p) => { setS((prev) => ({ ...prev, ...p })); setDirty(true); };
  const patchPrices = (p) => patch({ prices: { ...s.prices, ...p } });
  const patchFlags = (p) => patch({ line_flags: { ...s.line_flags, ...p } });
  const patchHeader = (p) => patch({ header: { ...s.header, ...p } });

  const save = async () => {
    const saved = await carteApi.saveSettings(s);
    setDirty(false); setS(saved);
    await refetchT();
    toast.success('Modèle enregistré');
    qc.invalidateQueries({ queryKey: ['carte-editions'] });
  };
  const newTemplate = async () => {
    const created = await carteApi.saveSettings({ name: 'Nouveau modèle' });
    await refetchT(); setId(created.id); setS(created); setDirty(false); setRenaming(true);
  };
  const duplicate = async () => {
    const { id: _, slug: __, is_published: ___, ...rest } = s;
    const created = await carteApi.saveSettings({ ...rest, name: `${s.name} (copie)` });
    await refetchT(); setId(created.id); setS(created); setDirty(false); setRenaming(true);
  };
  const doRemove = async () => {
    await carteApi.deleteSettings(id);
    setConfirmDel(false);
    setId(null); setS(null); setDirty(false);
    await refetchT();
    qc.invalidateQueries({ queryKey: ['carte-editions'] });
  };
  const uploadPage = async (where, file) => {
    const r = await carteApi.upload(file);
    patch({ [where]: [...(s[where] || []), r.id] });
    toast.success('Page ajoutée');
  };

  if (!consts || tLoading) return <div className="p-6"><Spinner /></div>;
  if (!templates.length) return <EmptyComposer onNew={newTemplate} />;
  if (!s) return <div className="p-6"><Spinner /></div>;

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-110px)]">
      {/* Panneau d'options */}
      <div className="lg:w-[400px] shrink-0 overflow-y-auto p-4 hair-r" data-testid="composer-options">
        {/* Modèle */}
        <div className="mb-4">
          {renaming ? (
            <input autoFocus className="vd-input h-9 w-full" defaultValue={s.name} data-testid="model-rename-input"
              onKeyDown={(e) => { if (e.key === 'Enter') { patch({ name: e.target.value }); setRenaming(false); } if (e.key === 'Escape') setRenaming(false); }}
              onBlur={(e) => { patch({ name: e.target.value }); setRenaming(false); }} />
          ) : (
            <select className="vd-input h-9 w-full" value={id || ''} onChange={(e) => { setId(e.target.value); setDirty(false); }} data-testid="model-select">
              {templates.map((t) => <option key={t.id} value={t.id}>{`${t.name} · ${LANGS[t.language] || t.language}`}</option>)}
            </select>
          )}
          <div className="flex flex-wrap gap-2 mt-2 text-[12px]">
            <button onClick={newTemplate} className="vd-link inline-flex items-center gap-1" data-testid="model-new"><Plus className="w-3.5 h-3.5" /> Nouveau</button>
            <button onClick={() => setRenaming(true)} className="vd-link inline-flex items-center gap-1" data-testid="model-rename"><Pencil className="w-3.5 h-3.5" /> Renommer</button>
            <button onClick={duplicate} className="vd-link inline-flex items-center gap-1" data-testid="model-duplicate"><Copy className="w-3.5 h-3.5" /> Dupliquer</button>
            <button onClick={() => setConfirmDel(true)} className="vd-link text-red-700 inline-flex items-center gap-1" data-testid="model-delete"><Trash2 className="w-3.5 h-3.5" /> Supprimer</button>
          </div>
          <label className="block mt-3">
            <span className="text-[11px] text-n600 block mb-1">Nom public <span className="text-n500">(affiché sur les onglets de la carte en ligne)</span></span>
            <input className="vd-input h-8 w-full text-[12.5px]" value={s.public_name || ''} onChange={(e) => patch({ public_name: e.target.value })}
              data-testid="opt-public-name" placeholder={s.name} />
          </label>
          <div className="flex items-center justify-between mt-3">
            <Button variant="primary" className="h-8" onClick={save} data-testid="model-save">Enregistrer</Button>
            <span className="text-[11px] text-n600" data-testid="model-status">{dirty ? 'Enregistrement automatique…' : `Enregistré ✓ · ${s.name}`}</span>
          </div>
        </div>

        <Opt label="Format de page">
          <div className="flex gap-1" data-testid="opt-format">
            <span className="px-3 h-8 inline-flex items-center rounded-full bg-gold100 border border-gold text-gold800 text-[12.5px]">A4 portrait</span>
          </div>
          <p className="text-[12px] text-n500 mt-2 leading-snug">210 × 297 mm · corps 2,5 cm à gauche et à droite · en-tête et pied à 1 cm</p>
        </Opt>

        <Opt label="Inclure">
          <Seg testid="opt-include-scope" value={s.include_scope || 'carte'} onChange={(v) => patch({ include_scope: v })}
            options={[{ value: 'cave', label: 'Toute la cave' }, { value: 'carte', label: 'À la carte & au verre' }]} />
          <div className="mt-3">
            <span className="text-[11px] text-n600 block mb-1">Quand c'est en rupture</span>
            <Seg testid="opt-rupture" value={s.rupture_mode || 'masquer'} onChange={(v) => patch({ rupture_mode: v })}
              options={[{ value: 'afficher', label: 'Ajouter [Rupture]' }, { value: 'masquer', label: 'Ne pas inclure dans la carte' }]} />
          </div>
        </Opt>

        <Opt label="Classement">
          <Seg testid="opt-group" value={s.group_by} onChange={(v) => patch({ group_by: v })}
            options={[{ value: 'couleur_region', label: 'Couleur › Région' }, { value: 'region', label: 'Région › Appellation' }]} />
        </Opt>
        <Opt label="Ordre des régions">
          <Seg testid="opt-region-order" value={s.region_order} onChange={(v) => patch({ region_order: v })}
            options={Object.entries(consts.region_orders).map(([value, label]) => ({ value, label }))} />
          {s.region_order === 'perso' && (
            <CustomOrder
              regions={[...new Set((preview?.sections || []).flatMap((sec) => sec.subsections.map((sub) => sub.region).filter(Boolean)))]}
              order={s.custom_order} onChange={(o) => patch({ custom_order: o })} />
          )}
        </Opt>

        <Opt label="Ordre du titre">
          <Seg testid="opt-title-order" value={s.title_order} onChange={(v) => patch({ title_order: v })}
            options={Object.entries(consts.title_orders).map(([value, label]) => ({ value, label }))} />
        </Opt>
        <Opt label="Ligne cuvée">
          <Check testid="opt-flag-spec" checked={s.line_flags.specificites} onChange={(v) => patchFlags({ specificites: v })} label="Spécificités (bio, coup de cœur)" />
          <Check testid="opt-flag-app" checked={s.line_flags.appellation_cepages} onChange={(v) => patchFlags({ appellation_cepages: v })} label="Appellation et cépages" />
          <Check testid="opt-flag-desc" checked={s.line_flags.description} onChange={(v) => patchFlags({ description: v })} label="Description" />
          <Check testid="opt-flag-accords" checked={s.line_flags.accords} onChange={(v) => patchFlags({ accords: v })} label="Accords mets-vins" />
        </Opt>

        <Opt label="Prix">
          <div className="flex flex-wrap gap-1.5" data-testid="opt-prices">
            <PricePill active={!s.prices.bouteille && !s.prices.verre && !s.prices.emporter}
              onClick={() => patchPrices({ bouteille: false, verre: false, emporter: false })} testid="opt-price-aucun">Aucun</PricePill>
            <PricePill active={s.prices.bouteille} onClick={() => patchPrices({ bouteille: !s.prices.bouteille })} testid="opt-price-bouteille">Bouteille</PricePill>
            <PricePill active={s.prices.verre} onClick={() => patchPrices({ verre: !s.prices.verre })} testid="opt-price-verre">Verre</PricePill>
            <PricePill active={s.prices.emporter} onClick={() => patchPrices({ emporter: !s.prices.emporter })} testid="opt-price-emporter">À emporter</PricePill>
          </div>
          <PriceRow label="Libellé">
            <div className="flex items-center gap-2">
              <Seg testid="opt-price-label" value={s.prices.label_mode} onChange={(v) => patchPrices({ label_mode: v })}
                options={[{ value: 'mention', label: 'Mention « verre »' }, { value: 'symbole', label: 'Symbole' }]} />
              {s.prices.label_mode === 'symbole' && (
                <SymbolPicker value={s.prices.verre_symbol || 'verre'} onChange={(v) => patchPrices({ verre_symbol: v })} />
              )}
            </div>
          </PriceRow>
          <PriceRow label="Format prix">
            <Seg testid="opt-price-layout" value={s.prices.layout} onChange={(v) => patchPrices({ layout: v })}
              options={[{ value: 'ligne', label: 'Sur une ligne' }, { value: 'dessous', label: 'Verre dessous' }]} />
          </PriceRow>
          <PriceRow label="Millésimes">
            <Seg testid="opt-price-multi" value={s.prices.multi_mode} onChange={(v) => patchPrices({ multi_mode: v })}
              options={[{ value: 'une_ligne', label: 'Une ligne cuvée' }, { value: 'par_millesime', label: 'Une ligne par millésime' }]} />
          </PriceRow>
          <p className="text-[11.5px] text-n500 mt-3 leading-snug">Colonne prix : année – bouteille – verre, un millésime par ligne.</p>
          <div className="mt-2"><Check testid="opt-price-euro" checked={s.prices.euro_after} onChange={(v) => patchPrices({ euro_after: v })} label="Symbole € après le prix" /></div>
        </Opt>

        <Opt label="Typographie">
          <Seg testid="opt-typo" value={s.typo_preset} onChange={(v) => patch({ typo_preset: v, typo: { ...consts.typo_presets_full[v] }, rule_color: consts.typo_presets_full[v].rule_color })}
            options={Object.entries(consts.typo_presets).map(([value, o]) => ({ value, label: o.label }))} />
          <TypoTabs typo={s.typo} onChange={(role, patchT) => patch({ typo: { ...s.typo, [role]: { ...s.typo[role], ...patchT } } })} />
        </Opt>

        <Opt label="En-tête et pied de page">
          <Seg testid="opt-header-mode" value={s.header?.mode || 'logo_texte'} onChange={(v) => patchHeader({ mode: v })}
            options={[{ value: 'logo_texte', label: 'Logo + texte' }, { value: 'bandeau', label: 'Bandeau image' }]} />
          {(s.header?.mode || 'logo_texte') === 'logo_texte' ? (
            <div className="mt-2 flex items-start gap-2">
              <label className="vd-plate aspect-[3/2] w-24 shrink-0 flex flex-col items-center justify-center cursor-pointer text-[11px] text-n500" data-testid="opt-logo">
                {s.header?.logo_file_id ? <img src={fileUrl(s.header.logo_file_id)} alt="logo" className="max-h-full max-w-full object-contain" /> : <><Upload className="w-4 h-4 mb-1" /> Logo</>}
                <input type="file" accept="image/png,image/svg+xml" className="hidden" onChange={async (e) => { if (e.target.files?.[0]) { const r = await carteApi.upload(e.target.files[0]); patchHeader({ logo_file_id: r.id }); } }} />
              </label>
              <input className="vd-input h-16 flex-1 text-[12px]" value={s.header?.slogan || ''} onChange={(e) => patchHeader({ slogan: e.target.value })} data-testid="opt-slogan" placeholder="Slogan (en italique)" />
            </div>
          ) : (
            <label className="mt-2 vd-btn vd-btn-secondary h-8 cursor-pointer text-[12px] inline-flex" data-testid="opt-banner">
              <Upload className="w-3.5 h-3.5" /> {s.header?.banner_file_id ? 'Bandeau importé ✓' : 'Importer un bandeau (210×35 mm)'}
              <input type="file" accept="image/png,application/pdf" className="hidden" onChange={async (e) => { if (e.target.files?.[0]) { const r = await carteApi.upload(e.target.files[0]); patchHeader({ banner_file_id: r.id }); } }} />
            </label>
          )}
          <input className="vd-input h-8 w-full mt-2 text-[12px]" value={s.footer_text || ''} onChange={(e) => patch({ footer_text: e.target.value })} data-testid="opt-footer" placeholder="Pied de page" />
        </Opt>

        <Opt label={`Pages avant (${(s.pages_before || []).length}) / après (${(s.pages_after || []).length})`}>
          <div className="flex gap-2">
            <label className="vd-btn vd-btn-secondary h-8 cursor-pointer text-[12px]" data-testid="opt-page-before"><Upload className="w-3.5 h-3.5" /> Avant
              <input type="file" accept=".pdf" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPage('pages_before', e.target.files[0])} /></label>
            <label className="vd-btn vd-btn-secondary h-8 cursor-pointer text-[12px]" data-testid="opt-page-after"><Upload className="w-3.5 h-3.5" /> Après
              <input type="file" accept=".pdf" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPage('pages_after', e.target.files[0])} /></label>
          </div>
        </Opt>
      </div>

      {/* Aperçu A4 */}
      <div className="flex-1 overflow-y-auto bg-surface p-4 md:p-8">
        <div className="flex items-center justify-between mb-3 max-w-[210mm] mx-auto">
          <span className="text-[12px] text-n600" data-testid="preview-meta">
            {consts.page.format} · {preview ? `${preview.total_count} vins` : '…'}
          </span>
          <Button variant="primary" className="h-9" onClick={async () => { if (dirty) { await carteApi.saveSettings(s); setDirty(false); } carteApi.generate(id, 'fr', s.name); }} data-testid="generate-pdf"><FileDown className="w-4 h-4" /> Générer le PDF</Button>
        </div>
        <div className="bg-white shadow-lg mx-auto" style={{ width: '210mm', minHeight: '297mm', padding: '10mm 0' }} data-testid="a4-sheet">
          {preview ? <CardPreview content={preview} print /> : <Spinner />}
        </div>
      </div>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce modèle&nbsp;?</AlertDialogTitle>
            <AlertDialogDescription>
              Le modèle « {s.name} » et ses éditions publiées seront supprimés définitivement. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doRemove} className="bg-red-700 hover:bg-red-800" data-testid="delete-confirm">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
